"use client"
import styled from "@emotion/styled"
import { Drawer } from "@mui/material"
import { FetchMenusQuery } from "apollo"
import { useLocale } from "next-intl"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useMemo, useState } from "react"
import { extractMenuProperties, getNonUmbracoUrl, mainMenuClick } from "utils"
import NavLink from "../../NavLink/NavLink"
import { Mobile } from "../../common/bit/MediaQueries"

const StyledDrawer = styled(Drawer)`
  z-index: 1000;

  & .MuiDrawer-paper {
    background-color: ${(props) => props.theme.colors.port.headerBg};
    -webkit-backdrop-filter: blur(8px) saturate(120%) brightness(100%);
    backdrop-filter: blur(8px) saturate(120%) brightness(100%);
  }
`

const MenuPanel = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
  padding: 16px 20px 32px;
  gap: 24px;
`

const MenuListContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-right: 4px;
`

const PortMenuList = styled.div`
  display: flex;
  flex-direction: column;
`

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 24px 0;
  background-color: ${(props) =>
    props.theme.colors.port?.mobileMenuDivider || "rgba(14, 40, 70, 0.16)"};
`

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SecondaryCta = styled.a<{ as?: React.ElementType }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 45px;
  background-color: transparent;
  color: ${(props) => props.theme.colors.navTextColor};
  border: 1px solid ${(props) => props.theme.colors.buttonSecondaryBorderColor};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  text-decoration: none;
  white-space: nowrap;
  width: fit-content;
  padding: 6px 24px;
  transition: background-color 0.2s ease-in-out;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 10px 24px;
  }
  &:hover {
    background-color: ${(props) => props.theme.colors.buttonSecondaryFilledBackground};
  }
  &:active {
    background-color: ${(props) => props.theme.colors.buttonSecondaryFilledBackground};
  }
`

export const StyledButton = styled.button`
  background-color: transparent;
  border: none;
  padding: 0px;
  height: 24px;
`
export const StyledList = styled.nav`
  display: flex;
  flex-direction: column;
  padding: 0px;
  padding-bottom: 22px;
  height: 100dvh;
  overflow: scroll;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 825px;
    overflow-y: hidden;
    overflow-x: auto;
    justify-content: space-between;
    flex-direction: row;
    height: auto;
    padding: 0px;
    gap: 24px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    max-width: 1055px;
  }
`
export const StyledLinkHeb = styled(Link)<{ selected: boolean }>`
  text-decoration: none;
  font-style: normal;
  font-weight: ${(props) => (props.selected ? "500" : "400")};
  font-size: 16px;
  line-height: 22px;

  color: ${(props) => props.theme.colors.black};
`

export const StyledLinkEn = styled(Link)<{ selected: boolean }>`
  position: relative;
  top: 1px;
  text-decoration: none;

  font-style: normal;
  font-weight: ${(props) => (props.selected ? "900" : "400")};
  font-size: 14px;
  line-height: 20px;

  color: ${(props) => props.theme.colors.black};
`
export const StyledLink = styled(Link)<{ selected?: boolean }>`
  display: flex;
`
export const StyledImageAndName = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 30px;
  padding: 0px 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 0px;
  }
`

type SecondaryCTALink = {
  url?: string
  name?: string
  target?: string
}

type Props = {
  // eslint-disable-next-line no-unused-vars
  setOpen: (open: boolean) => void
  open: boolean
  menuItems: FetchMenusQuery | any
  companyName?: string
  secondaryCta?: SecondaryCTALink
}

const supplementalKeywords = ["terms", "privacy", "accessibil", "תנאי", "מדיניות", "נגיש"]

const secondaryCtaName = (name?: string) => {
  let ctaName = name || ""
  if (ctaName.includes("*")) {
    ctaName = ctaName.replace("*", "")
    ctaName = ctaName + "*"
  }

  return ctaName
}

const SideMenu = ({ menuItems, open, setOpen, companyName, secondaryCta }: Props) => {
  const locale = useLocale()
  const pathname = usePathname()
  const [openedMenuIndex, setOpenedMenuIndex] = useState<number>(-1)
  const decodedPathname = decodeURI(pathname)
  const decodedWithSlash = `${decodedPathname}/`

  const menuSource = useMemo(() => {
    const typedMenu = menuItems as unknown as any
    if (typedMenu?.menu_mobile?.children) {
      return typedMenu.menu_mobile
    }

    if (typedMenu?.children) {
      return typedMenu
    }

    return undefined
  }, [menuItems])

  const menuChildren = useMemo(() => menuSource?.children ?? [], [menuSource])

  const partitionedMenus = useMemo(() => {
    return menuChildren.reduce(
      (acc: { primary: any[]; secondary: any[] }, item: any) => {
        const { title = "", url = "" } = extractMenuProperties(item)
        if (!title) return acc
        const lowerTitle = title.toLowerCase()
        const lowerUrl = (url || "").toLowerCase()
        const isSupplementary = supplementalKeywords.some(
          (keyword) => lowerTitle.includes(keyword) || lowerUrl.includes(keyword),
        )
        if (isSupplementary) {
          acc.secondary.push(item)
        } else {
          acc.primary.push(item)
        }
        return acc
      },
      { primary: [] as any[], secondary: [] as any[] },
    )
  }, [menuChildren])

  const languageLink = {
    title: locale === "he" ? "English" : "עִברִית",
    href:
      locale === "he" ? `/en${pathname === "/" ? "" : pathname}` : pathname.replace("/en", "/he"),
  }

  const renderNavItem = (item: any, index: number, section: "primary" | "secondary") => {
    const { title, url } = extractMenuProperties(item)
    if (!title) return null

    const subActive = item?.children?.some(
      (sub: any) => getNonUmbracoUrl(extractMenuProperties(sub).url) === decodedWithSlash,
    )
    const subSubActive = item?.children?.some((sub: any) =>
      sub?.children?.some(
        (subSub: any) => getNonUmbracoUrl(extractMenuProperties(subSub).url) === decodedWithSlash,
      ),
    )
    const navIndex = section === "primary" ? index : index + 1000

    return (
      <NavLink
        variant="port-mobile"
        onClick={() => {
          setOpen(false)
          item?.children?.length === 0 && mainMenuClick(title, "")
        }}
        key={`${section}-${index.toString()}`}
        href={getNonUmbracoUrl(url)}
        selected={decodedWithSlash === getNonUmbracoUrl(url) || subActive || subSubActive}
        title={title}
        subCategories={item?.children}
        isOpen={openedMenuIndex === navIndex}
        setOpen={() => {
          if (openedMenuIndex === navIndex) {
            setOpenedMenuIndex(-1)
          } else {
            setOpenedMenuIndex(navIndex)
          }
        }}
        navlinkdropdownprops={{
          background: "none !important",
          "& .MuiCollapse-root": {
            background: "none !important",
          },
        }}
        companyName={companyName}
      />
    )
  }

  return (
    <Mobile>
      <StyledDrawer
        anchor="top"
        open={open}
        onClose={() => {
          setOpen(false)
          mainMenuClick("mobile menu close", "")
        }}
        PaperProps={{
          sx: {
            paddingTop: {
              xs: "80px",
            },
            height: "max-content",
            zIndex: 100000,
          },
        }}
      >
        <MenuPanel>
          <MenuListContainer role="navigation">
            <PortMenuList>
              <NavLink
                variant="port-mobile"
                onClick={() => {
                  setOpen(false)
                  mainMenuClick(locale === "he" ? "עמוד הבית" : "Home Page", "")
                }}
                href={locale === "he" ? "/" : "/en"}
                selected={decodedPathname === "/" || decodedPathname === "/en"}
                title={locale === "he" ? "עמוד הבית" : "Home Page"}
                navlinkdropdownprops={{
                  background: "none !important",
                  WebkitBackdropFilter: "blur(8px) saturate(120%) brightness(100%)",
                  backdropFilter: "blur(8px) saturate(120%) brightness(100%)",
                }}
                isOpen={openedMenuIndex === 999}
                setOpen={() => {
                  if (openedMenuIndex === 999) {
                    setOpenedMenuIndex(-1)
                  } else {
                    setOpenedMenuIndex(999)
                  }
                }}
                companyName={companyName}
              />
              {partitionedMenus.primary.map((item: any, index: number) =>
                renderNavItem(item, index, "primary"),
              )}
            </PortMenuList>
            {(partitionedMenus.secondary.length > 0 || languageLink.title) && (
              <>
                <Divider />
                <FooterLinks>
                  {partitionedMenus.secondary.map((item: any, index: number) =>
                    renderNavItem(item, index, "secondary"),
                  )}
                  {secondaryCta?.url &&
                    secondaryCta?.name &&
                    (secondaryCta.url.startsWith("/") && !secondaryCta.url.startsWith("//") ? (
                      <SecondaryCta
                        as={Link}
                        href={getNonUmbracoUrl(secondaryCta.url)}
                        target={secondaryCta.target ?? undefined}
                        rel={secondaryCta.target === "_blank" ? "noreferrer" : undefined}
                        onClick={() => {
                          setOpen(false)
                          mainMenuClick(secondaryCta.name || "CTA", "")
                        }}
                      >
                        {secondaryCtaName(secondaryCta.name)}
                      </SecondaryCta>
                    ) : (
                      <SecondaryCta
                        href={getNonUmbracoUrl(secondaryCta.url)}
                        target={secondaryCta.target ?? undefined}
                        rel={secondaryCta.target === "_blank" ? "noreferrer" : undefined}
                        onClick={() => {
                          setOpen(false)
                          mainMenuClick(secondaryCta.name || "CTA", "")
                        }}
                      >
                        {secondaryCtaName(secondaryCta.name)}
                      </SecondaryCta>
                    ))}
                </FooterLinks>
              </>
            )}
          </MenuListContainer>
        </MenuPanel>
      </StyledDrawer>
    </Mobile>
  )
}

export default SideMenu
